import React, { useEffect, useState } from 'react';
import Spin from 'src/components/common/Spin';
import { Icon, Tooltip } from 'antd';
import useProductSummaryListFetch from '../hooks/useProductSummaryListFetch';
import usePriceSummaryInfoFetch from '../hooks/usePriceSummaryInfoFetch';
import ProductSummarySegment from '../types/ProductSummarySegment';
import MPITable from './MPITable';
import ErrorAlert from 'src/components/common/ErrorAlert';
import styles from './MPIList.module.less';

type MPIListProps = {
  matchType: string;
  ownerId: string;
  priceType: string;
  sourceOwnerId: string;
  sourceProductId: string;
  regionId: string;
};

const REGULAR_TYPE = '1';

const MPIList: React.FC<MPIListProps> = ({
  ownerId,
  sourceOwnerId,
  sourceProductId,
  matchType,
  priceType,
  regionId,
}) => {
  const [{ loading: productLoading, data: productData, error: productError }] = useProductSummaryListFetch({
    ownerId,
    sourceOwnerId,
    sourceProductId,
    matchType,
    priceType,
    regionId,
  });
  const [{ loading: priceLoading, data: priceData, error: priceError }] = usePriceSummaryInfoFetch({
    sourceOwnerId,
  });
  const [tableData, setTableData] = useState<ProductSummarySegment[]>([]);
  const isPriceRegular = priceType === REGULAR_TYPE;

  useEffect(() => {
    if (productData?.length) {
      const ownerSite = productData.find((item) => item.ownerId === 0);
      let transformedData = productData.filter((item) => item.ownerId !== 0);

      const sortFunction = (
        a: ProductSummarySegment,
        b: ProductSummarySegment,
        rankKey: keyof ProductSummarySegment
      ) => {
        const aRank = a[rankKey] as number | null;
        const bRank = b[rankKey] as number | null;

        if (aRank === null && bRank === null) {
          return (a.ownerName || '').localeCompare(b.ownerName || '');
        }
        if (aRank === null) return 1;
        if (bRank === null) return -1;

        const rankComparison = aRank - bRank;
        if (rankComparison !== 0) {
          return rankComparison;
        }
        return (a.ownerName || '').localeCompare(b.ownerName || '');
      };

      if (priceType === REGULAR_TYPE) {
        transformedData.sort((a, b) => sortFunction(a, b, 'marketRank'));
      } else {
        transformedData.sort((a, b) => sortFunction(a, b, 'lowestMarketRank'));
      }

      if (ownerSite) {
        transformedData = [
          {
            ...ownerSite,
            ownerName: 'Our Price',
          },
          ...transformedData,
        ];
      }

      setTableData(transformedData);
    } else {
      setTableData([]);
    }
  }, [productData, priceType]);

  const startDate = priceData?.startDuration || 'N/A';
  const endDate = priceData?.endDuration || 'N/A';
  const combinedLoading = productLoading || priceLoading;
  const combinedError = productError || priceError;

  return (
    <div>
      <Spin spinning={combinedLoading}>
        <div className={styles.header}>
          <Tooltip
            title={
              <div className={styles.tooltipContent}>
                <span>Data is based on the date range:</span>
                <span>
                  {startDate} to {endDate}
                </span>
              </div>
            }
          >
            <Icon type="info-circle" className={styles.topRightIcon} />
          </Tooltip>
        </div>
        <ErrorAlert isVisible={combinedError} errorLabel={'MPI'} />
        {productData && (
          <MPITable loading={combinedLoading} productSummaryList={tableData} isPriceRegular={isPriceRegular} />
        )}
        {tableData.length === 0 && <p className={styles.noResults}>No MPI data currently available.</p>}
      </Spin>
    </div>
  );
};

export default MPIList;
